import type { BaseMetadata, ThreadData } from "@liveblocks/client"
import { TableContent } from "@prismicio/types-internal/lib/content"
import { TableRow as TTTableRow } from "@tiptap/extension-table-row"
import type { Node as ProseMirrorNode, Schema } from "@tiptap/pm/model"

import { tableCellConverter, tableHeaderCellConverter } from "./TableCell"
import { tableCellAttrs } from "./TableCell/types"
import { getNodeFromSchema } from "./utils"

type TableRowBlock = TableContent["content"][number]
type TableCellOrHeaderBlock = TableRowBlock["content"][number]

export const TableRow = TTTableRow.extend({
  name: "tableRow",

  addAttributes() {
    return {
      ...this.parent?.(),
      key: { default: null },
    }
  },
})

export const tableRowConverter = {
  fromPrismic(
    id: string,
    schema: Schema,
    threads?: ThreadData<BaseMetadata>[],
  ) {
    return function (row: TableRowBlock): ProseMirrorNode {
      const node = getNodeFromSchema(schema, TTTableRow.name)
      return node.create(
        { key: row.key },
        row.content.map((data) => {
          switch (data.type) {
            case "tableHeader": {
              return tableHeaderCellConverter.fromPrismic(
                id,
                schema,
                threads,
              )(data)
            }
            case "tableCell": {
              return tableCellConverter.fromPrismic(id, schema, threads)(data)
            }
          }
        }),
      )
    }
  },

  toPrismic(node: ProseMirrorNode): TableRowBlock {
    const data = tableCellAttrs.parse(node.attrs)
    const content: TableCellOrHeaderBlock[] = []
    node.content.forEach((cell) => {
      switch (cell.type.name) {
        case "tableHeader": {
          content.push(tableHeaderCellConverter.toPrismic(cell))
          break
        }
        case "tableCell": {
          content.push(tableCellConverter.toPrismic(cell))
          break
        }
      }
    })

    return { key: data.key ?? crypto.randomUUID(), type: "tableRow", content }
  },
}
